import { type RequestServiceOptions, utils } from '@atlaskit/util-service-support';

import { generateHash } from '../../../utils/caching/common/key-generator';
import { promiseCache } from '../../../utils/caching/persist-refresh';

export const ASSETS_WORKSPACE_ID = 'assets-workspace-id';

interface AssetsWorkspaceIdResponse {
	results: {
		id: string;
	}[];
}

const { refreshOnAccessDecorator: refreshOnAccessDecoratorAssets } =
	promiseCache<AssetsWorkspaceIdResponse>({
		name: ASSETS_WORKSPACE_ID,
		expiration: 1000 * 60 * 60 * 24, // 24 hours
	});

// remove when the when start getting workspaceId from config
// https://hello.jira.atlassian.cloud/browse/AURUM-841
// https://hello.jira.atlassian.cloud/browse/ARIES-329
export async function getAssetsWorkspaceId(): Promise<string | null> {
	const url = '/rest/servicedesk/cmdb/latest/workspace';
	const options: RequestServiceOptions = {
		requestInit: {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		},
	};
	const key = generateHash(ASSETS_WORKSPACE_ID);

	const supplier = () => utils.requestService<AssetsWorkspaceIdResponse>({ url }, options);

	const response = await refreshOnAccessDecoratorAssets({
		key: key.toString(),
		supplier,
		returnExpired: false,
		onlyRefreshIfExpired: true,
	});
	const ids = response.results ?? [];
	return ids.length ? ids[0].id : null;
}

interface SchemasResponse {
	values: {
		id: string;
		globalId: string;
		name: string;
		workspaceId: string;
	}[];
}

export async function searchAssetsSchemas(query: string = '', maxResults: number = 3): Promise<{
    schemaLabel: string;
    ari: string;
    id: string;
    globalId: string;
    name: string;
    workspaceId: string;
}[]> {
	const workspaceId = await getAssetsWorkspaceId();
	if (!workspaceId) {
		// returning empty array as fallback
		return [];
	}
	const url = `/gateway/api/jsm/assets/workspace/${workspaceId}/v1/objectschema/list?maxResults=${maxResults}&includeCounts=false${query ? `&query=${query}` : ''}`;
	try {
		const response = await fetch(url, {
			method: 'GET',
			headers: { 'Content-Type': 'application/json' },
		});
		const responseData: SchemasResponse = await response.json();
		const schemas = responseData.values ?? [];
		const transformedSchemas = schemas.map((schema) => ({
			...schema,
			schemaLabel: schema.name,
			ari: `ari:cloud:cmdb::schema/${workspaceId}/${schema.id}`,
		}));
		return transformedSchemas;
	} catch (error) {
		// fire analytics event
		return [];
	}
}

interface TypesResponse {
	data: {
		id: string;
		globalId: string;
		name: string;
		workspaceId: string;
		objectSchemaId: string;
		schemaLabel: string;
		description: string;
		icon: {
			id: string;
			name: string;
			url16: string;
			url48: string;
		};
		created: string;
		updated: string;
	}[];
	pagination: {
		totalItems: string;
		totalPages: string;
		currentPage: string;
		pageSize: string;
		hasNextPage: boolean;
		hasPreviousPage: boolean;
	};
}

export async function searchAssetsTypes(query: string = '', maxResults: number = 3): Promise<{
    ari: string;
    id: string;
    globalId: string;
    name: string;
    workspaceId: string;
    objectSchemaId: string;
    schemaLabel: string;
    description: string;
    icon: {
        id: string;
        name: string;
        url16: string;
        url48: string;
    };
    created: string;
    updated: string;
}[]> {
	const workspaceId = await getAssetsWorkspaceId();
	if (!workspaceId) {
		// returning empty array as fallback
		return [];
	}
	const url = `/gateway/api/jsm/assets/workspace/${workspaceId}/v1/objecttype/all/list?pageSize=${maxResults}${query ? `&filterBy=${query}` : ''}`;
	try {
		const response = await fetch(url, {
			method: 'GET',
			headers: { 'Content-Type': 'application/json' },
		});
		const responseData: TypesResponse = await response.json();
		const types = responseData.data ?? [];
		const transformedTypes = types.map((type) => ({
			...type,
			ari: `ari:cloud:cmdb::type/${workspaceId}/${type.id}`,
		}));
		return transformedTypes;
	} catch (error) {
		// fire analytics event
		return [];
	}
}
